import React, { useEffect, useState, useRef } from 'react';

/**
 * ChainConnector - Draws professional curved SVG paths connecting chained processes
 * Features: Smooth bezier curves, animated flow indicators, gradients, and hover effects
 */
const ChainConnector = ({ items, currentDate, getDaysInMonth }) => {
  const [connections, setConnections] = useState([]);
  const [hoveredChain, setHoveredChain] = useState(null);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!items || items.length === 0) return;

    const updateConnections = () => {
      const chainGroups = {};
      items.forEach(item => {
        if (item.chainId && item.isChained && item.date) {
          if (!chainGroups[item.chainId]) {
            chainGroups[item.chainId] = [];
          }
          chainGroups[item.chainId].push(item);
        }
      });

      const newConnections = [];
      const calendarContainer = document.querySelector('.calendar-grid');
      if (!calendarContainer) return;

      const containerRect = calendarContainer.getBoundingClientRect();

      Object.entries(chainGroups).forEach(([chainId, chainItems]) => {
        const sortedItems = chainItems.sort((a, b) => a.chainOrder - b.chainOrder);

        for (let i = 0; i < sortedItems.length - 1; i++) {
          const fromItem = sortedItems[i];
          const toItem = sortedItems[i + 1];

          const fromElement = document.querySelector(`[data-item-id="${fromItem.uniqueId}"]`);
          const toElement = document.querySelector(`[data-item-id="${toItem.uniqueId}"]`);

          if (fromElement && toElement) {
            const fromRect = fromElement.getBoundingClientRect();
            const toRect = toElement.getBoundingClientRect();

            const from = {
              x: fromRect.right - containerRect.left,
              y: fromRect.top + fromRect.height / 2 - containerRect.top,
            };

            const to = {
              x: toRect.left - containerRect.left,
              y: toRect.top + toRect.height / 2 - containerRect.top,
            };

            // Calculate control points for smooth bezier curve
            const dx = to.x - from.x;
            const dy = to.y - from.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // Adaptive control point offset based on distance
            const controlOffset = Math.min(distance * 0.4, 100);

            const controlPoint1 = {
              x: from.x + controlOffset,
              y: from.y,
            };

            const controlPoint2 = {
              x: to.x - controlOffset,
              y: to.y,
            };

            newConnections.push({
              chainId,
              from,
              to,
              controlPoint1,
              controlPoint2,
              chainOrder: i,
              totalInChain: sortedItems.length - 1,
              color: getChainColor(chainId),
              fromItem,
              toItem,
              distance,
            });
          }
        }
      });

      setConnections(newConnections);
    };

    updateConnections();

    const handleUpdate = () => requestAnimationFrame(updateConnections);
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [items, currentDate]);

  const getChainColor = (chainId) => {
    const colors = [
      { primary: '#3b82f6', secondary: '#60a5fa', light: '#93c5fd' }, // blue
      { primary: '#10b981', secondary: '#34d399', light: '#6ee7b7' }, // green
      { primary: '#f59e0b', secondary: '#fbbf24', light: '#fcd34d' }, // amber
      { primary: '#ef4444', secondary: '#f87171', light: '#fca5a5' }, // red
      { primary: '#8b5cf6', secondary: '#a78bfa', light: '#c4b5fd' }, // purple
      { primary: '#ec4899', secondary: '#f472b6', light: '#f9a8d4' }, // pink
      { primary: '#14b8a6', secondary: '#2dd4bf', light: '#5eead4' }, // teal
      { primary: '#f97316', secondary: '#fb923c', light: '#fdba74' }, // orange
    ];

    const hash = chainId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Generate smooth bezier path
  const generatePath = (conn) => {
    return `M ${conn.from.x} ${conn.from.y}
            C ${conn.controlPoint1.x} ${conn.controlPoint1.y},
              ${conn.controlPoint2.x} ${conn.controlPoint2.y},
              ${conn.to.x} ${conn.to.y}`;
  };

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 pointer-events-none z-30"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <defs>
        {/* Arrow markers for each color */}
        {connections.map((conn, idx) => (
          <marker
            key={`arrow-${conn.chainId}-${idx}`}
            id={`arrowhead-${conn.chainId}-${conn.chainOrder}`}
            markerWidth="12"
            markerHeight="12"
            refX="10"
            refY="6"
            orient="auto"
            markerUnits="strokeWidth"
          >
            <path
              d="M 0 0 L 12 6 L 0 12 L 3 6 Z"
              fill={conn.color.primary}
              opacity="0.9"
            />
          </marker>
        ))}

        {/* Gradient definitions for each connection */}
        {connections.map((conn, idx) => (
          <linearGradient
            key={`gradient-${conn.chainId}-${idx}`}
            id={`gradient-${conn.chainId}-${conn.chainOrder}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor={conn.color.primary} stopOpacity="0.9" />
            <stop offset="50%" stopColor={conn.color.secondary} stopOpacity="0.95" />
            <stop offset="100%" stopColor={conn.color.primary} stopOpacity="0.9" />
          </linearGradient>
        ))}

        {/* Animated flow gradient */}
        {connections.map((conn, idx) => (
          <linearGradient
            key={`flow-${conn.chainId}-${idx}`}
            id={`flow-gradient-${conn.chainId}-${conn.chainOrder}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="0%"
          >
            <stop offset="0%" stopColor={conn.color.light} stopOpacity="0">
              <animate
                attributeName="offset"
                values="0;1"
                dur="2s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="50%" stopColor={conn.color.light} stopOpacity="0.8">
              <animate
                attributeName="offset"
                values="0.5;1.5"
                dur="2s"
                repeatCount="indefinite"
              />
            </stop>
            <stop offset="100%" stopColor={conn.color.light} stopOpacity="0">
              <animate
                attributeName="offset"
                values="1;2"
                dur="2s"
                repeatCount="indefinite"
              />
            </stop>
          </linearGradient>
        ))}

        {/* Glow filter for highlighted chains */}
        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {connections.map((conn, index) => {
        const isHovered = hoveredChain === conn.chainId;
        const pathString = generatePath(conn);

        return (
          <g
            key={`${conn.chainId}-${conn.chainOrder}`}
            onMouseEnter={() => setHoveredChain(conn.chainId)}
            onMouseLeave={() => setHoveredChain(null)}
            className="transition-all duration-300"
            style={{ pointerEvents: 'stroke' }}
          >
            {/* Shadow/glow effect */}
            <path
              d={pathString}
              fill="none"
              stroke={conn.color.primary}
              strokeWidth="8"
              opacity="0.2"
              filter={isHovered ? "url(#glow)" : "none"}
              className="transition-all duration-300"
            />

            {/* Main connection path */}
            <path
              d={pathString}
              fill="none"
              stroke={`url(#gradient-${conn.chainId}-${conn.chainOrder})`}
              strokeWidth={isHovered ? "5" : "4"}
              strokeLinecap="round"
              strokeLinejoin="round"
              markerEnd={`url(#arrowhead-${conn.chainId}-${conn.chainOrder})`}
              className="transition-all duration-300"
              style={{
                filter: isHovered ? 'drop-shadow(0 0 6px rgba(0,0,0,0.3))' : 'none',
              }}
            />

            {/* Animated flow indicator */}
            <path
              d={pathString}
              fill="none"
              stroke={`url(#flow-gradient-${conn.chainId}-${conn.chainOrder})`}
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={isHovered ? "0.9" : "0.6"}
              className="transition-opacity duration-300"
            />

            {/* Connection nodes */}
            <g>
              {/* Start node */}
              <circle
                cx={conn.from.x}
                cy={conn.from.y}
                r={isHovered ? "7" : "6"}
                fill={conn.color.primary}
                stroke="white"
                strokeWidth="2"
                className="transition-all duration-300"
                style={{
                  filter: isHovered ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none',
                }}
              />

              {/* Start node inner glow */}
              <circle
                cx={conn.from.x}
                cy={conn.from.y}
                r="4"
                fill={conn.color.secondary}
                opacity="0.6"
              />

              {/* End node */}
              <circle
                cx={conn.to.x}
                cy={conn.to.y}
                r={isHovered ? "7" : "6"}
                fill={conn.color.primary}
                stroke="white"
                strokeWidth="2"
                className="transition-all duration-300"
                style={{
                  filter: isHovered ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' : 'none',
                }}
              />

              {/* End node inner glow */}
              <circle
                cx={conn.to.x}
                cy={conn.to.y}
                r="4"
                fill={conn.color.secondary}
                opacity="0.6"
              />
            </g>

            {/* Progress indicator - shows position in chain */}
            {isHovered && (
              <g>
                <text
                  x={(conn.from.x + conn.to.x) / 2}
                  y={(conn.from.y + conn.to.y) / 2 - 15}
                  textAnchor="middle"
                  fill={conn.color.primary}
                  fontSize="11"
                  fontWeight="bold"
                  className="pointer-events-none"
                  style={{
                    textShadow: '0 1px 3px rgba(255,255,255,0.8)',
                  }}
                >
                  Step {conn.chainOrder + 1} of {conn.totalInChain}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
};

export default ChainConnector;
